import React, { useState } from "react";
import { Box, Button, Typography, Alert, LinearProgress, Paper } from "@mui/material";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import rolesData from "../seed-data/roles.json";
import pagesData from "../seed-data/pages.json";
import foldersData from "../seed-data/folders.json";
import pageItems1 from "../seed-data/pageItems1.json";
import pageItems2 from "../seed-data/pageItems2.json";
import pageItems3 from "../seed-data/pageItems3.json";
import pageItems4 from "../seed-data/pageItems4.json";

export default function ImportSeedData() {
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);

  const addLog = (message) => {
    setLogs((prev) => [...prev, message]);
  };

  const handleImport = async () => {
    setLoading(true);
    setStatus("");
    setError("");
    setLogs([]);
    setProgress(0);

    try {
      addLog("🚀 Starting seed data import...");

      // Import roles
      addLog("\n📝 Importing roles...");
      for (const [id, data] of Object.entries(rolesData)) {
        await setDoc(doc(db, "roles", id), data);
        addLog(`  ✓ Created role: ${id}`);
      }
      setProgress(25);

      // Import pages
      addLog("\n📄 Importing pages...");
      for (const [id, data] of Object.entries(pagesData)) {
        await setDoc(doc(db, "pages", id), data);
        addLog(`  ✓ Created page: ${id}`);
      }
      setProgress(50);

      // Import folders
      addLog("\n📁 Importing folders...");
      for (const [id, data] of Object.entries(foldersData)) {
        await setDoc(doc(db, "folders", id), data);
        addLog(`  ✓ Created folder: ${id}`);
      }
      setProgress(75);

      // Import pageItems
      addLog("\n🔧 Importing page items...");
      const allPageItems = { ...pageItems1, ...pageItems2, ...pageItems3, ...pageItems4 };
      
      for (const [id, data] of Object.entries(allPageItems)) {
        await setDoc(doc(db, "pageItems", id), data);
        addLog(`  ✓ Created page item: ${id}`);
      }
      setProgress(100);

      addLog("\n✅ Seed data import completed successfully!");
      addLog(`\n📊 Summary:`);
      addLog(`  - Roles: ${Object.keys(rolesData).length}`);
      addLog(`  - Pages: ${Object.keys(pagesData).length}`);
      addLog(`  - Folders: ${Object.keys(foldersData).length}`);
      addLog(`  - Page Items: ${Object.keys(allPageItems).length}`);
      addLog("\n🎉 Your Algo ISO portal is ready!");

      setStatus("Import completed successfully! You can now navigate to /home to see your portal.");
    } catch (err) {
      console.error("Error importing seed data:", err);
      setError(`Error: ${err.message}`);
      addLog(`\n❌ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: "#f5f5f5",
        p: 3,
      }}
    >
      <Box sx={{ maxWidth: 800, width: "100%", bgcolor: "white", p: 4, borderRadius: 2, boxShadow: 3 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
          Import Algo ISO Seed Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          This will import 4 roles, 4 pages, 9 folders, and 25 page items to Firestore
        </Typography>

        {loading && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
              {progress}% complete
            </Typography>
          </Box>
        )}

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleImport}
          disabled={loading}
          sx={{ mb: 3 }}
        >
          {loading ? "Importing..." : "Import Seed Data"}
        </Button>

        {status && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {status}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {logs.length > 0 && (
          <Paper
            sx={{
              p: 2,
              bgcolor: "#1e1e1e",
              color: "#d4d4d4",
              fontFamily: "monospace",
              fontSize: "12px",
              maxHeight: 400,
              overflow: "auto",
            }}
          >
            {logs.map((log, index) => (
              <div key={index}>{log}</div>
            ))}
          </Paper>
        )}
      </Box>
    </Box>
  );
}
